'use client';

import { Button } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import { useMediaQuery } from '@shared/utils';
import Image, { type ImageProps } from 'next/image';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { LotterySchedule } from '../lotteries';
import { JackpotLabel, type JackpotLabelSize } from '../lotteries/JackpotLabel/JackpotLabel';
import { LotteryPrice } from '../lotteries/LotteryPrice/LotteryPrice';
import { StageProvider, useStage, type BreakpointValues, type StageVariant } from './context/StageCtx';

type StageImageProps = ComponentProps<'div'> & {
  image: Omit<ImageProps, 'className' | 'fill' | 'sizes'>;
  imageBehaviour?: 'cover' | 'contain';
};

type StageProps = ComponentProps<'div'> & { variant?: StageVariant; breakpoints?: BreakpointValues };

type StageContentBodyDrawDateProps = Omit<ComponentProps<typeof LotterySchedule>, 'text'> & { children?: string };
/**
 * StageImage component renders an image within a responsive container.
 * It's expected to be used inside the Stage component.
 * Extends the props of the <div> component.
 *
 * @param image - The image to display in the StageImage component.
 * @param imageBehaviour - The behaviour of the image ('cover' | 'contain').
 * @returns The div element with the image inside.
 */
function StageImage({ className, image, imageBehaviour = 'cover', ...props }: StageImageProps) {
  const imageClass = imageBehaviour === 'cover' ? 'object-cover' : 'object-contain';
  return (
    <div className={twMerge('relative md:col-start-2 md:col-span-1 w-full h-47 md:h-82.5 xl:h-95', className)} {...props}>
      <Image {...image} fill sizes="(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1024px) 500px, 640px" className={twMerge(`mx-auto`, imageClass)} />
    </div>
  );
}

/**
 * StageContent component renders the content container for the Stage component.
 * It's expected to be used inside the Stage component.
 * Extends the props of the <div> component.
 * @returns The styled div element with the content inside.
 */
function StageContent({ className, ...props }: ComponentProps<'div'>) {
  return <div className={twMerge('p-5 md:pl-9 lg:pl-10 xl:pl-15 md:row-start-1 flex flex-col gap-4', className)} {...props} />;
}

/**
 * StageContentHeadline component renders the headline for the Stage component.
 * It's expected to be used inside the StageContent component.
 * Extends the props of the <h3> component.
 * @returns The styled h3 element with the headline inside.
 */
function StageContentHeadline({ className, children, ...props }: ComponentProps<'h3'>) {
  return (
    <h3 className={twMerge('heading-4 xl:heading-2', className)} {...props}>
      {children}
    </h3>
  );
}

/**
 * StageContentBody component renders the body for the Stage component.
 * It's expected to be used inside the StageContent component.
 * Extends the props of the <div> component.
 * @returns The div element with the body inside.
 */
function StageContentBody({ ...props }: ComponentProps<'div'>) {
  return <div {...props} />;
}

/**
 * StageContentBodyDrawDate component renders the draw date for the Stage component.
 * It's expected to be used inside the StageContentBody component.
 * Extends the props of the <p> component.
 * This component makes use of the useStage hook to determine the variant of the Stage component and apply styles based on the variant.
 * @returns The styled p element with the draw date inside.
 */
function StageContentBodyDrawDate({ date, lotteryName, className, children, ...props }: StageContentBodyDrawDateProps) {
  const { variant } = useStage();
  const classNames = twMerge('paragraph-strong', variant === 'headline' ? 'mb-1' : 'mb-2.5 lg:mb-6', className);
  return <LotterySchedule date={date} lotteryName={lotteryName} text={children || ''} className={classNames} data-testid="stage-drawdate-lottery-schedule" {...props} />;
}

/**
 * StageContentBodyLotteryName component renders the lottery name for the Stage component.
 * It's expected to be used inside the StageContentBody component.
 * Extends the props of the <h5> component.
 * This component makes use of the useStage hook to determine the variant of the Stage component and apply styles based on the variant.
 * @returns The styled h5 element with the lottery name inside.
 */
function StageContentBodyLotteryName({ className, children, ...props }: ComponentProps<'h5'>) {
  const { variant } = useStage();
  const classNames = twMerge('-mb-1', variant === 'headline' ? 'xl:heading-4' : 'md:heading-4 lg:heading-3 lg:mb-1', className);
  return (
    <h5 className={classNames} {...props}>
      {children}
    </h5>
  );
}

/**
 * StageContentBodyJackpot component renders the jackpot for the Stage component.
 * It's expected to be used inside the StageContentBody component.
 * Extends the props of the <JackpotLabel> component.
 * This component makes use of the useStage hook to determine the variant of the Stage component and apply styles based on the variant.
 * @returns The JackpotLabel component with the jackpot inside.
 */
function StageContentBodyJackpot({ ...props }: Omit<ComponentProps<typeof JackpotLabel>, 'jackpotLabelSize'>) {
  const {
    variant,
    breakpoints: { sm, md, lg, xl },
  } = useStage();
  const isBelowSm = useMediaQuery(`(width < ${sm}px)`);
  const isBelowMd = useMediaQuery(`(width < ${md}px)`);
  const isBelowLg = useMediaQuery(`(width < ${lg}px)`);
  const isBelowXl = useMediaQuery(`(width < ${xl}px)`);
  const sizes: Record<StageVariant, string> = {
    headline: isBelowXl ? 'small' : 'medium',
    jackpot: isBelowSm ? 'small' : isBelowMd ? 'medium' : isBelowLg ? 'large' : 'extra-large',
  };
  return <JackpotLabel jackpotLabelSize={sizes[variant] as JackpotLabelSize} {...props} />;
}

/**
 * StageContentBodyLabel component renders the label for the Stage component.
 * It's expected to be used inside the StageContentBody component.
 * Extends the props of the <p> component.
 * @returns The styled p element with the label inside.
 */
function StageContentBodyLabel({ className, ...props }: ComponentProps<'p'>) {
  return <p className={twMerge('paragraph-strong mt-1 lg:paragraph-l-strong lg:mt-2.5', className)} {...props} />;
}

/**
 * StageContentFooter component renders the footer for the Stage component.
 * It's expected to be used inside the StageContent component.
 * Extends the props of the <div> component.
 * @returns The styled div element with the footer inside.
 */
function StageContentFooter({ className, ...props }: ComponentProps<'div'>) {
  return <div className={twMerge('flex flex-col gap-3 items-start', className)} {...props} />;
}

/**
 * StageContentFooterActions component renders the actions for the Stage component.
 * It's expected to be used inside the StageContentFooter component.
 * Extends the props of the <div> component.
 * @returns The styled div element with the actions inside.
 */
function StageContentFooterActions({ className, ...props }: ComponentProps<'div'>) {
  return <div className={twMerge('flex gap-4 content-start items-center', className)} {...props} />;
}

/**
 * StageContentFooterActionsButton component renders the button for the Stage component.
 * It's expected to be used inside the StageContentFooterActions component.
 * Extends the props of the <Link> and variant of the <Button> component.
 * @returns The Button component with the button inside.
 */
function StageContentFooterActionsButton({ href, children, variant = 'primary', ...props }: ComponentProps<typeof Link> & Pick<ComponentProps<typeof Button>, 'variant'>) {
  const { breakpoints } = useStage();
  const isBelowXl = useMediaQuery(`(width < ${breakpoints.xl}px)`);
  return (
    <Button asChild variant={variant} size={isBelowXl ? 'm' : 'l'}>
      <Link href={href} data-track-name="CTA" {...props}>
        {children}
      </Link>
    </Button>
  );
}

/**
 * StageContentFooterActionsPrice component renders the price for the Stage component.
 * It's expected to be used inside the StageContentFooterActions component.
 * Extends the props of the <LotteryPrice> component.
 * @returns The LotteryPrice component with the price inside.
 */
function StageContentFooterActionsPrice({ ...props }: Omit<ComponentProps<typeof LotteryPrice>, 'priceVariant'>) {
  return <LotteryPrice {...props} priceVariant="label" />;
}

/**
 * StageContentFooterDisclaimer component renders the disclaimer for the Stage component.
 * It's expected to be used inside the StageContentFooter component.
 * Extends the props of the <p> component.
 * @returns The styled p element with the disclaimer inside.
 */
function StageContentFooterDisclaimer({ className, ...props }: ComponentProps<'p'>) {
  return <p className={twMerge('paragraph-s', className)} {...props} />;
}

/**
 * Stage component renders the main Stage component.
 * It's expected to be used as a wrapper for the Stage component.
 * Extends the props of the <div> component.
 * This component makes use of the StageProvider to provide the variant and breakpoints to the descendant components.
 * @returns The styled div element with the Stage component inside.
 */
function Stage({ className, variant, breakpoints, ...props }: StageProps) {
  return (
    <StageProvider variant={variant} breakpoints={breakpoints}>
      <div className={twMerge('bg-container rounded-lg overflow-hidden grid grid-cols-1 items-center md:grid-cols-2', className)} data-track-container="Stage" {...props} />
    </StageProvider>
  );
}

export {
  Stage,
  StageContent,
  StageContentBody,
  StageContentBodyDrawDate,
  StageContentBodyJackpot,
  StageContentBodyLabel,
  StageContentBodyLotteryName,
  StageContentFooter,
  StageContentFooterActions,
  StageContentFooterActionsButton,
  StageContentFooterActionsPrice,
  StageContentFooterDisclaimer,
  StageContentHeadline,
  StageImage,
};
